import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, ClockIcon, PlusIcon, ShieldAlertIcon } from 'lucide-react';
import type { HealthStatus, Monitor } from '../types/monitor';
import { StatusBadge } from './StatusBadge';
import { Sparkline } from './Sparkline';
import { certificateDaysRemaining, overallStatus, sslStatus, uptimeStatus } from '../utils/health';
import { formatAvailability, relativeTime } from '../utils/format';
const rail: Record<HealthStatus, string> = {
  healthy: 'bg-healthy',
  warning: 'bg-warn',
  critical: 'bg-critical',
  paused: 'bg-paused'
};
interface MonitorRowProps {
  monitor: Monitor;
  atLimit: boolean;
  onEnable: (kind: 'ssl' | 'uptime') => void;
}
function DisabledCell({
  kind,
  atLimit,
  onEnable
}: {
  kind: 'ssl' | 'uptime';
  atLimit: boolean;
  onEnable: () => void;
}) {
  return <div className="flex items-center gap-2" data-id="element-407" data-mp="31c3aophssaoo">
      <span className="rounded-control border border-dashed border-line-strong px-2 py-1 text-[11px] text-ink-soft" data-id="element-408" data-mp="2x96ybgiupat6">
        Not monitored
      </span>
      {atLimit ? <span className="text-[11px] text-ink-soft" data-id="element-409" data-mp="11tq15q53vc4t">plan full</span> : <button type="button" onClick={onEnable} className="inline-flex items-center gap-1 rounded-control px-1.5 py-1 text-[11px] font-medium text-brand transition-colors duration-150 ease-snap hover:bg-brand-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand" data-id="element-410" data-mp="11rmzu9yi5wks">
          <PlusIcon className="h-3 w-3" aria-hidden="true" data-id="element-411" data-mp="1kj61accvp2kp" />
          Enable {kind === 'ssl' ? 'SSL' : 'uptime'}
        </button>}
    </div>;
}
export function MonitorRow({
  monitor,
  atLimit,
  onEnable
}: MonitorRowProps) {
  const status = overallStatus(monitor);
  const ssl = sslStatus(monitor);
  const uptime = uptimeStatus(monitor);
  const days = certificateDaysRemaining(monitor);
  return <li className="relative" data-id="element-412" data-mp="55rzv5sjrx9">
      <div className="grid grid-cols-1 items-center gap-4 border-b border-line px-4 py-4 transition-colors duration-150 ease-snap last:border-b-0 hover:bg-canvas/70 md:grid-cols-[1.6fr_1.1fr_1.3fr_auto] md:px-5" data-id="element-413" data-mp="6gulrni2tms">
        <span className={`absolute left-0 top-3 h-[calc(100%-1.5rem)] w-[3px] rounded-r ${rail[status]}`} aria-hidden="true" data-id="element-414" data-mp="2245ajagqln8" />

        <div className="min-w-0 pl-3" data-id="element-415" data-mp="3t6u5y96h998s">
          <Link to={`/app/monitors/${monitor.id}`} className="truncate text-sm font-semibold text-ink transition-colors duration-150 ease-snap hover:text-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand" data-id="element-416" data-mp="b2shr9k7kdy0">
            {monitor.domain}
          </Link>
          <p className="mt-1 flex items-center gap-1.5 truncate text-xs text-ink-soft" data-id="element-417" data-mp="futq4ke99kir">
            <ClockIcon className="h-3 w-3 shrink-0" aria-hidden="true" data-id="element-418" data-mp="urujstg0bpjv" />
            <span className="truncate" data-id="element-419" data-mp="prydgnju7m15">
              {monitor.label} · checked {relativeTime(monitor.lastCheckedAt)}
            </span>
          </p>
        </div>

        <div data-id="element-420" data-mp="19bdsnblzessf">
          {!monitor.sslEnabled ? <DisabledCell kind="ssl" atLimit={atLimit} onEnable={() => onEnable('ssl')} data-id="element-421" data-mp="1dfd7lprm5jjo" /> : monitor.certificate ? <div data-id="element-422" data-mp="14e5cfgke8ytt">
              <StatusBadge status={ssl} label={days !== null && days < 0 ? `Expired ${Math.abs(days)}d ago` : `${days}d remaining`} data-id="element-423" data-mp="14dlzke9lp0mn" />
              <p className="mt-1 truncate text-xs text-ink-soft" data-id="element-424" data-mp="ui2aobtgf27e">
                {monitor.certificate.issuer}
              </p>
            </div> : <div data-id="element-425" data-mp="lop4i86dq43d">
              <StatusBadge status="critical" label="Unreadable" data-id="element-426" data-mp="lo5rn5vl65w7" />
              <p className="mt-1 flex items-center gap-1 text-xs text-critical-ink" data-id="element-427" data-mp="6roec93u6h70">
                <ShieldAlertIcon className="h-3 w-3" aria-hidden="true" data-id="element-428" data-mp="bpzn0giealnn" />
                Handshake failed
              </p>
            </div>}
        </div>

        <div data-id="element-429" data-mp="1a1ke62oq1uki">
          {!monitor.uptimeEnabled ? <DisabledCell kind="uptime" atLimit={atLimit} onEnable={() => onEnable('uptime')} data-id="element-430" data-mp="1a2vgrz6fkwa1" /> : <div className="flex items-center gap-3" data-id="element-431" data-mp="10c97snt7z40b">
              <div className="w-[86px] shrink-0" data-id="element-432" data-mp="10bhe5ij6ngco">
                <StatusBadge status={uptime} label={monitor.isUp ? 'Up' : 'Down'} data-id="element-433" data-mp="vcwmw8lws70d" />
                <p className="tabular mt-1 text-xs text-ink-soft" data-id="element-434" data-mp="r9hwco0cg88o">
                  {formatAvailability(monitor.availability24h)} · 24h
                </p>
              </div>
              <Sparkline values={monitor.responseTimes} label={`Recent response times for ${monitor.domain}`} data-id="element-435" data-mp="hdwwowiqzk23" />
            </div>}
        </div>

        <Link to={`/app/monitors/${monitor.id}`} aria-label={`Open ${monitor.domain}`} className="hidden h-8 w-8 items-center justify-center rounded-control text-ink-soft transition-colors duration-150 ease-snap hover:bg-canvas hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand md:inline-flex" data-id="element-436" data-mp="2brxwbjm4gb38">
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-437" data-mp="2xy0b9qeci17a" />
        </Link>
      </div>
    </li>;
}